import * as React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Stack,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

export interface TestData {
  title: string;
  contents: string[];
}

export default function TestingContentEditor({
  value,
  onChange,
}: {
  value: TestData[] | null;
  onChange: (value: TestData[]) => void;
}) {
  const [local, setLocal] = useState<TestData[] | null>(null);

  // keep local cache in sync when prop changes
  useEffect(() => {
    setLocal(value ? [...value] : null);
  }, [value]);

  const ensureArray = (): TestData[] => (local ? [...local] : []);

  const addTheme = () => {
    const next = ensureArray();
    next.push({ title: "", contents: [] });
    setLocal(next);
    onChange(next);
  };

  const removeTheme = (i: number) => {
    const next = ensureArray().filter((_, idx) => idx !== i);
    setLocal(next.length ? next : []);
    onChange(next);
  };

  const addUnterpunkt = (i: number) => {
    const next = ensureArray();
    if (!next[i]) return;
    next[i] = { ...next[i], contents: [...next[i].contents, ""] };
    setLocal(next);
    onChange(next);
  };

  const removeUnterpunkt = (i: number, j: number) => {
    const next = ensureArray();
    if (!next[i]) return;
    const contents = next[i].contents.filter((_, idx) => idx !== j);
    next[i] = { ...next[i], contents };
    setLocal(next);
    onChange(next);
  };

  const updateTitle = (i: number, text: string) => {
    const next = ensureArray();
    if (!next[i]) return;
    next[i] = { ...next[i], title: text };
    setLocal(next);
  };

  const updateUnterpunkt = (i: number, j: number, text: string) => {
    const next = ensureArray();
    if (!next[i]) return;
    const contents = [...next[i].contents];
    contents[j] = text;
    next[i] = { ...next[i], contents };
    setLocal(next);
  };

  const handleBlur = () => {
    onChange(local ?? []);
  };

  const isEmpty = !local || local.length === 0;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {isEmpty ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography>Nur Betriebsstundenerfassung</Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {local!.map((item, i) => (
            <Box
              key={`theme-${i}`}
              className="lbm_part"
              sx={{
                display: "flex",
                flex: 1,
                flexDirection: "column",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Box className="lbm_title" sx={{ p: 1.25 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Titel ${i + 1}`}
                  value={item.title}
                  onChange={(e) => updateTitle(i, e.target.value)}
                  onBlur={handleBlur}
                />
              </Box>

              <Box
                className="lbm_list"
                sx={{
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                  overflow: "auto",
                }}
              >
                {item.contents.map((c, j) => (
                  <Box
                    key={`unterpunkt-${i}-${j}`}
                    className="lbm_element"
                    sx={{
                      display: "flex",
                      columnGap: "8px",
                      alignItems: "center",
                      p: 1,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label={`Unterpunkt ${i + 1}.${j + 1}`}
                      value={c}
                      onChange={(e) => updateUnterpunkt(i, j, e.target.value)}
                      onBlur={handleBlur}
                    />
                    <IconButton
                      aria-label="Unterpunkt löschen"
                      size="small"
                      onClick={() => removeUnterpunkt(i, j)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    p: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => addUnterpunkt(i)}
                    size="small"
                    startIcon={<AddIcon />}
                  >
                    Unterpunkt hinzufügen
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => removeTheme(i)}
                    size="small"
                    startIcon={<DeleteOutlineIcon />}
                  >
                    Wartungsthema löschen
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Box textAlign="right">
        <Button variant="outlined" onClick={addTheme} startIcon={<AddIcon />}>
          Wartungsthema hinzufügen
        </Button>
      </Box>
    </Box>
  );
}
